import React, { createContext, useContext, useState, useEffect } from 'react'
import { authService, User } from '../services/authService'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>
  signup: (email: string, password: string, username?: string, recaptchaToken?: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  logout: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  setUser: (user: User | null) => void
  refreshUser: () => Promise<void>
  requestPasswordReset: (email: string, recaptchaToken?: string) => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  resendVerificationEmail: (email: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await authService.getCurrentUser()
        setUserState(currentUser)
      } catch (error) {
        console.error('Auth check failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (email: string, password: string, recaptchaToken?: string) => {
    setIsLoading(true)
    try {
      const response = await authService.login(email, password, recaptchaToken)
      if (response.user) {
        setUserState(response.user)
      }
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const signup = async (email: string, password: string, username?: string, recaptchaToken?: string) => {
    setIsLoading(true)
    try {
      const response = await authService.signup(email, password, username, recaptchaToken)
      if (response.user) {
        setUserState(response.user)
      }
    } catch (error) {
      console.error('Signup failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithGoogle = async () => {
    try {
      const response = await authService.getGoogleAuthUrl()
      if (response.authUrl) {
        window.location.href = response.authUrl
      }
    } catch (error) {
      console.error('Google login failed:', error)
      throw error
    }
  }

  const logout = async () => {
    setIsLoading(true)
    try {
      await authService.logout()
      setUserState(null)
    } catch (error) {
      console.error('Logout failed, trying force logout:', error)
      // If normal logout fails, try force logout
      try {
        await authService.forceLogout()
        setUserState(null)
      } catch (forceLogoutError) {
        console.error('Force logout also failed:', forceLogoutError)
        // Even if force logout fails, clear the user state
        setUserState(null)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUserState({ ...user, ...userData })
    }
  }

  const setUser = (newUser: User | null) => {
    setUserState(newUser)
  }

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser()
      setUserState(currentUser)
    } catch (error) {
      console.error('Failed to refresh user:', error)
      throw error
    }
  }

  const requestPasswordReset = async (email: string, recaptchaToken?: string) => {
    try {
      await authService.requestPasswordReset(email, recaptchaToken)
    } catch (error) {
      console.error('Password reset request failed:', error)
      throw error
    }
  }

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await authService.resetPassword(token, newPassword)
    } catch (error) {
      console.error('Password reset failed:', error)
      throw error
    }
  }

  const resendVerificationEmail = async (email: string) => {
    try {
      await authService.resendVerificationEmail(email)
    } catch (error) {
      console.error('Resend verification email failed:', error)
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    signup,
    loginWithGoogle,
    logout,
    updateUser,
    setUser,
    refreshUser,
    requestPasswordReset,
    resetPassword,
    resendVerificationEmail,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 